import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './donut-chart.style.css';

/**
 * Custom Donut Chart Component - Replaces recharts
 * Lightweight, customizable, and fits the app design system
 */
function DonutChart({ data, title, showLegend = true }) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);

  // Calculate total and percentages
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);

  // Calculate angles for each segment
  let currentAngle = -90; // Start from top
  const segments = data.map((item, index) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    const angle = (item.value / total) * 360;
    const segment = {
      ...item,
      percentage,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
      angle
    };
    currentAngle += angle;
    return segment;
  });

  // Create SVG path for donut segment
  const createArc = (startAngle, endAngle, innerRadius, outerRadius) => {
    const start = polarToCartesian(0, 0, outerRadius, endAngle);
    const end = polarToCartesian(0, 0, outerRadius, startAngle);
    const innerStart = polarToCartesian(0, 0, innerRadius, endAngle);
    const innerEnd = polarToCartesian(0, 0, innerRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
      "M", start.x, start.y,
      "A", outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
      "L", innerEnd.x, innerEnd.y,
      "A", innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
      "Z"
    ].join(" ");

    return d;
  };

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const baseRadius = 80;
  const innerRadius = 50;
  const outerRadius = activeIndex !== null ? baseRadius + 10 : baseRadius;

  return (
    <div className="donut-chart-container">
      {title && <h4 className="donut-chart-title">{title}</h4>}

      <div className="donut-chart-wrapper">
        <svg viewBox="-120 -120 240 240" className="donut-chart-svg">
          {segments.map((segment, index) => {
            const isActive = activeIndex === index;
            const radius = isActive ? outerRadius : baseRadius;

            return (
              <g key={index}>
                <path
                  d={createArc(segment.startAngle, segment.endAngle, innerRadius, radius)}
                  fill={segment.color || '#6B8CAE'}
                  stroke="#23252B"
                  strokeWidth="2"
                  className="donut-segment"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    filter: isActive ? 'brightness(1.1)' : 'none'
                  }}
                />
              </g>
            );
          })}

          {/* Center label */}
          <text
            x="0"
            y="0"
            textAnchor="middle"
            dominantBaseline="middle"
            className="donut-center-text"
          >
            {t('financial.total')}
          </text>
          <text
            x="0"
            y="15"
            textAnchor="middle"
            dominantBaseline="middle"
            className="donut-center-value"
          >
            €{total.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
          </text>
        </svg>
      </div>

      {showLegend && (
        <div className="donut-chart-legend">
          {segments.map((segment, index) => (
            <div
              key={index}
              className={`legend-item ${activeIndex === index ? 'active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <div
                className="legend-color"
                style={{ backgroundColor: segment.color || '#6B8CAE' }}
              />
              <div className="legend-label">
                <span className="legend-name">{segment.name}</span>
                <span className="legend-value">
                  €{segment.value.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}
                  {' '}({segment.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string
    })
  ).isRequired,
  title: PropTypes.string,
  showLegend: PropTypes.bool
};

export default DonutChart;

